import React, { useState, Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/card';
import { Button } from 'shadcn/components/ui/button';
import { generateRoomId } from './room-setup/lib/utils';
import { useNavigate } from 'react-router-dom';
import { ROOM_PATH } from '../constants';
import { useParams } from 'react-router-dom';
import { isCustomMeeting } from './room-setup/lib/utils';

const TEXT_CONFIG = {
  interview: {
    mainTitle: 'Welcome to the Interview Process',
    preSectionTitle: 'Pre-Interview Guidelines',
    instructionsTitle: 'Interview Instructions',
    window: 'interview',
    buttonText: 'Start Interview',
    processText: 'interview',
    instructions: (
      <>
        <li>Click the "Start Interview" button when you are ready to begin.</li>
        <li>
          Answer each question thoughtfully and concisely, ensuring your responses are clear and
          complete.
        </li>
        <li>Maintain a professional demeanor throughout the entire interview process.</li>
      </>
    ),
    showCheatingSection: true,
  },
  meeting: {
    mainTitle: 'Welcome to the Meeting Process',
    preSectionTitle: 'Pre-Meeting Guidelines',
    instructionsTitle: 'Meeting Instructions',
    window: 'meeting',
    buttonText: 'Start Meeting',
    processText: 'meeting',
    instructions: (
      <>
        <li>Click the "Start Meeting" button when you are ready to begin.</li>
        <li>Participate actively and ensure your contributions are clear and concise.</li>
        <li>Maintain a professional demeanor throughout the entire meeting process.</li>
      </>
    ),
    showCheatingSection: false,
  },
};

const LiveInterviewHome: React.FC = () => {
  const navigate = useNavigate();
  const startMeeting = () => {
    const roomName = generateRoomId();
    navigate(`${ROOM_PATH}/${roomName}`);
  };

  const { interviewId } = useParams<{ interviewId: string }>();
  const custom = isCustomMeeting(interviewId);

  const contextKey = custom ? 'meeting' : 'interview';
  const text = TEXT_CONFIG[contextKey];

  return (
    <div className="w-full md:p-6 lg:p-8 bg-white flex items-start justify-center">
      <Card className="w-full max-w-4xl shadow-lg bg-white">
        <CardHeader className="pb-4 md:pb-6">
          <CardTitle className="text-2xl md:text-3xl font-bold text-center text-black underline">
            {text.mainTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <section>
            <h2 className="text-lg md:text-xl font-semibold mb-2 text-black">
              {text.preSectionTitle}
            </h2>
            <ul className="space-y-1.5 text-gray-700 list-disc pl-4">
              <li>
                Choose a quiet, well-lit environment free from distractions and background noises.
              </li>
              <li>
                Ensure a stable internet connection and test your microphone and camera beforehand.
              </li>
              <li>
                Close unnecessary applications and browser tabs, keep the {text.window} window
                active at all times.
              </li>
              <li>Avoid switching tabs or minimizing the window during the {text.window}.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg md:text-xl font-semibold mb-2 text-black">
              {text.instructionsTitle}
            </h2>
            <ul className="space-y-1.5 text-gray-700 list-disc pl-4">{text.instructions}</ul>
          </section>

          {text.showCheatingSection && (
            <section>
              <h2 className="text-lg md:text-xl font-semibold mb-2 text-red-700">
                Cheating & Fair Play
              </h2>
              <ul className="space-y-1.5 text-red-700 list-disc pl-4">
                <li>
                  Answer all questions in your own words, AI-generated responses or external help
                  are strictly prohibited.
                </li>
                <li>
                  Switching tabs, using other applications, or seeking outside assistance during the
                  interview is not allowed.
                </li>
                <li>
                  Any detected misconduct may result in immediate disqualification from the process.
                </li>
              </ul>
            </section>
          )}

          <div className="flex justify-center">
            <Button
              onClick={startMeeting}
              className="bg-black hover:bg-gray-800 text-white px-8 py-2.5 rounded-full transition-all duration-300 ease-in-out transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {text.buttonText}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveInterviewHome;
